import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type SelectOption = {
  value: string | number | null;
  label: string;
  description?: string;
  disabled?: boolean;
};

type UseSearchableSelectProps = {
  options: SelectOption[];
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (option: SelectOption) => void;
  /** When true, keep options in the order supplied by the caller (no label sort). */
  preserveOptionOrder?: boolean;
};

export function useSearchableSelect({
  options,
  isOpen,
  onClose,
  onSelect,
  preserveOptionOrder = false,
}: UseSearchableSelectProps) {
  const sortedOptions = useMemo(
    () =>
      preserveOptionOrder ? options : [...options].sort((a, b) => a.label.localeCompare(b.label)),
    [options, preserveOptionOrder],
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<SelectOption[]>(sortedOptions);
  const [dropdownPosition, setDropdownPosition] = useState<"bottom" | "top">("bottom");
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<Map<number, HTMLLIElement>>(new Map());

  // Calculate fixed position for the dropdown based on container's bounding rect
  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = 300; // Max height from CSS

    const position: "bottom" | "top" =
      spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? "top" : "bottom";

    setDropdownPosition(position);

    if (position === "bottom") {
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    } else {
      setDropdownStyle({
        position: "fixed",
        bottom: window.innerHeight - rect.top + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  // Update filtered options when options prop changes
  useEffect(() => {
    setFilteredOptions(sortedOptions);
  }, [sortedOptions]);

  // Handle local filtering (sorted by label)
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    const filtered = sortedOptions.filter(opt =>
      opt.label.toLowerCase().includes(query.toLowerCase()),
    );
    setFilteredOptions(filtered);
    setHighlightedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setHighlightedIndex(prev => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
          break;
        case "ArrowUp":
          event.preventDefault();
          setHighlightedIndex(prev => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
          break;
        case "Enter":
          event.preventDefault();
          if (
            highlightedIndex >= 0 &&
            highlightedIndex < filteredOptions.length &&
            !filteredOptions[highlightedIndex].disabled
          ) {
            onSelect?.(filteredOptions[highlightedIndex]);
          }
          break;
        case "Escape":
          event.preventDefault();
          onClose();
          break;
        case "Tab":
          onClose();
          break;
      }
    },
    [isOpen, filteredOptions, highlightedIndex, onClose, onSelect],
  );

  // Scroll highlighted option into view after state updates
  useEffect(() => {
    optionRefs.current.get(highlightedIndex)?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  // Close dropdown when clicking outside (check both container and portal'd dropdown)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideContainer = containerRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideContainer && !insideDropdown) {
        onClose();
        setSearchQuery("");
        setFilteredOptions(sortedOptions);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [sortedOptions, onClose]);

  // Focus search input and calculate dropdown position when dropdown opens
  useEffect(() => {
    if (isOpen) {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
      setSearchQuery("");
      setFilteredOptions(sortedOptions);
      updateDropdownPosition();
    }
  }, [isOpen, sortedOptions, updateDropdownPosition]);

  // Reposition dropdown on scroll (capture phase for any ancestor) and resize
  useEffect(() => {
    if (!isOpen) return;

    const handleScrollOrResize = () => {
      updateDropdownPosition();
    };

    window.addEventListener("scroll", handleScrollOrResize, { capture: true, passive: true });
    window.addEventListener("resize", handleScrollOrResize, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, {
        capture: true,
      } as EventListenerOptions);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen, updateDropdownPosition]);

  return {
    searchQuery,
    filteredOptions,
    dropdownPosition,
    dropdownStyle,
    highlightedIndex,
    containerRef,
    dropdownRef,
    searchInputRef,
    optionRefs,
    handleSearchChange,
    handleKeyDown,
  };
}
